import React from "react";
import { Helmet } from "react-helmet";
import Img from "gatsby-image";
import { graphql, useStaticQuery } from "gatsby";

import Content from "../components/Content";
import { useWelshMap } from "../useWelsh";
import * as classes from "./Pages.module.css";

import fig1 from "../documents/vol2/Fig 1 - Site Location Map.pdf";
import fig2 from "../documents/vol2/Fig 2 - Site Location Plan.pdf";
import fig3 from "../documents/vol2/Fig 3 - Field Numbers.pdf";
import fig4 from "../documents/vol2/Fig 4 - Site Proposals.pdf";
import fig5 from "../documents/vol2/Fig 5 - Indicative Infra A3.pdf";
import fig6 from "../documents/vol2/Fig 6 - Indicative Infra A1.pdf";
import fig7 from "../documents/vol2/Fig 7 - Access Track Detail.pdf";
import fig8 from "../documents/vol2/Fig 8 - Construction Compound Detail.pdf";
import fig9 from "../documents/vol2/Fig 9 - PV Module & Rack Detail.pdf";
import fig10 from "../documents/vol2/Fig 10 - Security Fence Detail.pdf";
import fig11 from "../documents/vol2/Fig 11 - CCTV.pdf";
import fig12 from "../documents/vol2/Fig 12 - Transformer & Switchgear Station Detail.pdf";
import fig13 from "../documents/vol2/Fig 13 - 132kV Compound.pdf";
import fig14 from "../documents/vol2/Fig 14 - Sections.pdf";
import fig15 from "../documents/vol2/Fig 15 - Cumulative Map.pdf";

import app1a from "../documents/vol3/Vol 3 - TA 1A - LVA (with appendices) compressed.pdf";
import app1b from "../documents/vol3/Vol 3 - TA 1B - GIS.pdf";
import app2a from "../documents/vol3/Vol 3 - TA 2A - EcA (with appendices).pdf";
import app2b from "../documents/vol3/Vol 3 - TA 2B - EDS.pdf";
import app3 from "../documents/vol3/Vol 3 - TA 3 - CHIA (with appendices).pdf";
import app4 from "../documents/vol3/Vol 3 - TA 4 - FCA-DS (with appendices).pdf";
import app5 from "../documents/vol3/Vol 3 - TA 5 - CTMP (with appendices).pdf";
import app6 from "../documents/vol3/Vol 3 - TA 6 - NIA (with appendices).pdf";
import app7 from "../documents/vol3/Vol 3 - TA 7  G&G (with appendices).pdf";
import app8 from "../documents/vol3/Vol 3 - TA 8 - OCEMP (with appendices).pdf";
import app9 from "../documents/vol3/Vol 3 - TA 9 - ALC.pdf";
import app10a from "../documents/vol3/Vol 3 - TA 10A - TCP (with appendices).pdf";
import app10b from "../documents/vol3/Vol 3 - TA 10B - AIA (with appendices).pdf";

const map = {
  english: {
    title: "Project Documents",
    text: `All of our draft planning application documents are available to view below.`,
  },
  welsh: {
    title: "Dogfennau’r Prosiect",
    text: `All of our draft planning application documents are available to view below.`,
  },
};

const Documents = () => {
  const text = useWelshMap(map);
  const { solarpanels } = useStaticQuery(graphql`
    query {
      solarpanels: file(relativePath: { eq: "solarpanels2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  return (
    <>
      <Helmet>
        <title>Penpergwm Solar Farm | Project Documents </title>
      </Helmet>
      <Content>
        <Img
          className={classes.image}
          fluid={solarpanels.childImageSharp.fluid}
          alt="Solar panels"
        />
        <h1>{text.title}</h1>
        <p>{text.text}</p>
        <h3>Volume 1: Planning Reports</h3>
        <p>We will make sure these are available as soon as possible.</p>
        <ul>
          <li>Covering Letter</li>
          <li>Application Form</li>
          <li>Design and Access Statement</li>
          <li>Alternative Site Assessment</li>
        </ul>
        <h3>Volume 2: Drawings</h3>
        <ul>
          <li>
            <a href={fig1}>Figure 1 – Site Location Map</a>
          </li>
          <li>
            <a href={fig2}>Figure 2 – Site Location Plan</a>
          </li>
          <li>
            <a href={fig3}>Figure 3 – Field Numbers</a>
          </li>
          <li>
            <a href={fig4}>Figure 4 – Site Proposals</a>
          </li>
          <li>
            <a href={fig5}>Figure 5 – Indicative Infra A3</a>
          </li>
          <li>
            <a href={fig6}>Figure 6 – Indicative Infra A1</a>
          </li>
          <li>
            <a href={fig7}>Figure 7 – Access Track Detail</a>
          </li>
          <li>
            <a href={fig8}>Figure 8 – Construction Compound Detail</a>
          </li>
          <li>
            <a href={fig9}>Figure 9 – PV Module and Rack Detail</a>
          </li>
          <li>
            <a href={fig10}>Figure 10 – Security Fence Detail</a>
          </li>
          <li>
            <a href={fig11}>Figure 11 – CCTV</a>
          </li>
          <li>
            <a href={fig12}>
              Figure 12 – Transformer and Switchgear Station Detail
            </a>
          </li>
          <li>
            <a href={fig13}>Figure 13 – 132 kV Compound</a>
          </li>
          <li>
            <a href={fig14}>Figure 14 – Sections</a>
          </li>
          <li>
            <a href={fig15}>Figure 15 – Cumulative Map</a>
          </li>
        </ul>
        <h3>Volume 3: Technical Appendices</h3>
        <ul>
          <li>
            <a href={app1a}>Appendix 1A – Landscape and Visual Assessment</a>
          </li>
          <li>
            <a href={app1b}>Appendix 1B – Green Infrastructure Strategy</a>
          </li>
          <li>
            <a href={app2a}>Appendix 2A – Ecological Assessment</a>
          </li>
          <li>
            <a href={app2b}>Appendix 2B – Ecological Design Strategy</a>
          </li>
          <li>
            <a href={app3}>Appendix 3 – Cultural Heritage Assessment</a>
          </li>
          <li>
            <a href={app4}>
              Appendix 4 – Flood Risk Assessment and Drainage Strategy
            </a>
          </li>
          <li>
            <a href={app5}>
              Appendix 5 – Construction Traffic Management Plan (CTMP)
            </a>
          </li>
          <li>
            <a href={app6}>Appendix 6 – Noise Assessment</a>
          </li>
          <li>
            <a href={app7}>Appendix 7 – Glint and Glare Assessment</a>
          </li>
          <li>
            <a href={app8}>
              Appendix 8 – Outline Construction Environmental Management Plan
            </a>
          </li>
          <li>
            <a href={app9}>
              Appendix 9 – Agricultural Land Classification Report
            </a>
          </li>
          <li>
            <a href={app10a}>Appendix 10a – Tree Survey</a>
          </li>
          <li>
            <a href={app10b}>
              Appendix 10b – Arboricultural Impact Assessment{" "}
            </a>
          </li>
        </ul>
      </Content>
    </>
  );
};

export default Documents;
